import React, { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import ChangePasswordModal from "./ChangePasswordModal";
import {
  User,
  Lock,
  LogOut,
  ChevronDown,
  Bell,
  Search,
  Sun,
  Moon,
  Monitor,
} from "lucide-react";

interface HeaderProps {
  collapsed?: boolean;
  onToggleSidebar?: () => void;
}

type Theme = "light" | "dark" | "system";

const Header: React.FC<HeaderProps> = ({ collapsed }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [passwordModal, setPasswordModal] = useState(false);
  const [themeDropdownOpen, setThemeDropdownOpen] = useState(false);
  const [currentTheme, setCurrentTheme] = useState<Theme>("light");

  useEffect(() => {
    const savedTheme = localStorage.getItem("moko-hr-theme") as Theme;
    if (!savedTheme) {
      setCurrentTheme("light");
      applyTheme("light");
      localStorage.setItem("moko-hr-theme", "light");
      return;
    }
    setCurrentTheme(savedTheme);
    if (savedTheme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light";
      applyTheme(systemTheme);
    } else {
      applyTheme(savedTheme as "light" | "dark");
    }
  }, []);

  const applyTheme = (theme: "light" | "dark") => {
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  };

  const handleThemeChange = (theme: Theme) => {
    setCurrentTheme(theme);
    localStorage.setItem("moko-hr-theme", theme);
    if (theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light";
      applyTheme(systemTheme);
    } else {
      applyTheme(theme);
    }
    setThemeDropdownOpen(false);
    toast.success(`Theme changed to ${theme}`);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      toast.error("Logout failed");
    }
  };

  const getRoleName = (role: string) =>
    ({
      admin: "Administrator",
      hr_manager: "HR Manager",
      hr_staff: "HR Staff",
      employee: "Employee",
    }[role] || role);

  // Build breadcrumb from path
  const getBreadcrumb = () => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    if (pathSegments.length === 0) return [{ label: "Dashboard", path: "/" }];

    const crumbs: { label: string; path: string }[] = [
      { label: "Home", path: "/" },
    ];

    const labelMap: Record<string, string> = {
      employees: "Employees",
      add: "Add New",
      profiles: "Profiles",
      leaves: "Leave",
      all: "All",
      calendar: "Calendar",
      payroll: "Payroll",
      "all-payslips": "All Payslips",
      history: "History",
      loans: "Loans",
      applications: "Applications",
      reports: "Reports",
      analytics: "Analytics",
      admin: "Administration",
      settings: "Settings",
      credentials: "User Management",
      logs: "System Logs",
      "my-payslips": "My Payslips",
      "leave-types": "Leave Types",
      "exchange-rates": "Exchange Rates",
      profile: "Profile",
    };

    let currentPath = "";
    for (const segment of pathSegments) {
      currentPath += `/${segment}`;
      crumbs.push({
        label: labelMap[segment] || segment.charAt(0).toUpperCase() + segment.slice(1),
        path: currentPath,
      });
    }

    return crumbs;
  };

  const breadcrumbs = getBreadcrumb();
  const pageTitle = breadcrumbs[breadcrumbs.length - 1]?.label || "Dashboard";

  const getThemeIcon = () => {
    switch (currentTheme) {
      case "dark":
        return <Moon size={16} className="text-indigo-400" />;
      case "system":
        return <Monitor size={16} className="text-gray-500" />;
      default:
        return <Sun size={16} className="text-amber-500" />;
    }
  };

  return (
    <>
      <header
        className={`
          fixed top-0 h-16 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 z-40
          flex items-center transition-all duration-300
          ${collapsed ? "left-[68px] right-0" : "left-64 right-0"}
        `}
      >
        <div className="flex items-center justify-between w-full px-6">
          {/* Left: Breadcrumb */}
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
              {pageTitle}
            </h1>
            <nav className="flex items-center gap-1 text-xs text-gray-400">
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={crumb.path}>
                  {index > 0 && <span className="mx-1">/</span>}
                  {index === breadcrumbs.length - 1 ? (
                    <span className="text-gray-600 dark:text-gray-300 font-medium">
                      {crumb.label}
                    </span>
                  ) : (
                    <button
                      onClick={() => navigate(crumb.path)}
                      className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                    >
                      {crumb.label}
                    </button>
                  )}
                </React.Fragment>
              ))}
            </nav>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative hidden md:block">
              <Search
                size={15}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search..."
                className="pl-9 pr-4 py-2 bg-slate-50 dark:bg-gray-800 rounded-lg text-sm border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 w-56 transition-all"
              />
            </div>

            {/* Theme Toggle */}
            <div className="relative">
              <button
                onClick={() => setThemeDropdownOpen(!themeDropdownOpen)}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700 transition-colors"
                title={`Theme: ${currentTheme}`}
              >
                {getThemeIcon()}
              </button>

              {themeDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-30"
                    onClick={() => setThemeDropdownOpen(false)}
                  />
                  <div className="absolute right-0 mt-2 w-40 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-40 overflow-hidden py-1">
                    {(
                      [
                        { key: "light", label: "Light", icon: <Sun size={14} /> },
                        { key: "dark", label: "Dark", icon: <Moon size={14} /> },
                        { key: "system", label: "System", icon: <Monitor size={14} /> },
                      ] as const
                    ).map((t) => (
                      <button
                        key={t.key}
                        onClick={() => handleThemeChange(t.key)}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                          currentTheme === t.key
                            ? "bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 font-medium"
                            : "text-gray-700 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-700"
                        }`}
                      >
                        {t.icon}
                        {t.label}
                        {currentTheme === t.key && (
                          <div className="ml-auto w-1.5 h-1.5 bg-indigo-500 rounded-full" />
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Notifications */}
            <button
              className="p-2 hover:bg-slate-100 dark:hover:bg-gray-800 rounded-lg transition-colors relative border border-gray-200 dark:border-gray-700"
              title="Notifications"
            >
              <Bell size={16} className="text-gray-500 dark:text-gray-400" />
              <div className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
            </button>

            {/* Divider */}
            <div className="w-px h-8 bg-gray-200 dark:bg-gray-700" />

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setDropdownOpen((prev) => !prev)}
                className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-slate-50 dark:hover:bg-gray-800 transition-colors"
              >
                <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center text-white text-xs font-bold">
                  {user?.firstName?.[0]}
                  {user?.lastName?.[0]}
                </div>
                <div className="hidden lg:block text-left">
                  <p className="text-sm font-medium text-gray-900 dark:text-white leading-tight">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">
                    {getRoleName(user?.role || "")}
                  </p>
                </div>
                <ChevronDown
                  size={14}
                  className={`text-gray-400 transition-transform duration-200 ${
                    dropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {dropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-30"
                    onClick={() => setDropdownOpen(false)}
                  />
                  <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-40 overflow-hidden">
                    {/* User Info */}
                    <div className="p-4 border-b border-gray-100 dark:border-gray-700">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-indigo-600 text-white flex items-center justify-center rounded-lg font-bold text-sm">
                          {user?.firstName?.[0]}
                          {user?.lastName?.[0]}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                            {user?.firstName} {user?.lastName}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {user?.email}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="py-1">
                      <button
                        onClick={() => {
                          setDropdownOpen(false);
                          navigate("/profile");
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <User size={16} className="text-gray-400" />
                        Profile Settings
                      </button>
                      <button
                        onClick={() => {
                          setDropdownOpen(false);
                          setPasswordModal(true);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <Lock size={16} className="text-gray-400" />
                        Change Password
                      </button>
                    </div>

                    <div className="border-t border-gray-100 dark:border-gray-700 py-1">
                      <button
                        onClick={() => {
                          setDropdownOpen(false);
                          handleLogout();
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
                      >
                        <LogOut size={16} />
                        Sign Out
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <ChangePasswordModal
        isOpen={passwordModal}
        onClose={() => setPasswordModal(false)}
      />
    </>
  );
};

export default Header;
